import type { FabricPrincipal } from "@fabric-harness/sdk";
import {
  newTokenCredentials,
  tokenProviderFn,
  type Credentials,
  type Header,
  type Token,
  type TokenCredentials,
} from "@databricks/sdk-auth";
import {
  defaultCredentials,
  newM2mCredentials,
  newPatCredentials,
} from "@databricks/sdk-auth/credentials";
import { resolve as resolveDatabricksProfile, type Profile } from "@databricks/sdk-core/profiles";
import { databricksWorkspaceOrigin } from "./workspace-origin.js";

/**
 * A bearer-token resolver awaited per request. Fabric uses the same native-SDK credential across
 * generated service clients, model calls, preview streaming, and Lakebase credential exchange.
 */
export type DatabricksTokenProvider = () => Promise<string>;

export type DatabricksPrincipal =
  /** Personal access token. Dev / single-user only. */
  | { kind: "pat"; token: string; label?: string }
  /** OAuth machine-to-machine (client-credentials). The agent acts as one governed identity. */
  | {
      kind: "service-principal";
      host: string;
      clientId: string;
      clientSecret: string;
      /** OAuth scope; defaults to `all-apis`. */
      scope?: string;
      /** Refresh this many seconds before expiry to avoid using a token that expires mid-flight. Default 60. */
      refreshSkewSeconds?: number;
    }
  /**
   * On-behalf-of a specific end user. The caller binds the user's token resolver (e.g. from a
   * channel `actor`), so Unity Catalog enforces *that human's* grants. The resolver is responsible
   * for its own caching/refresh.
   */
  | { kind: "on-behalf-of"; userToken: () => string | Promise<string>; label?: string }
  | {
      /**
       * Local developer identity resolved by the Databricks CLI OAuth cache.
       * When `host` is omitted, the workspace host is resolved from the named
       * CLI profile (`~/.databrickscfg` / `DATABRICKS_CONFIG_FILE`). An
       * explicit `host` takes precedence as the workspace origin used for API
       * calls, but CLI token acquisition always follows the named profile
       * (`databricks auth token --profile <name>`, no host argument) — `host`
       * and the profile must point at the same workspace.
       */
      kind: "cli-profile";
      host?: string;
      profile?: string;
    };

export interface DatabricksPrincipalEnvOptions {
  /** PAT variable name. Default `DATABRICKS_TOKEN`. */
  tokenEnv?: string;
  /** OAuth client id variable name. Default `DATABRICKS_CLIENT_ID`. */
  clientIdEnv?: string;
  /** OAuth client secret variable name. Default `DATABRICKS_CLIENT_SECRET`. */
  clientSecretEnv?: string;
  /** Workspace host variable name. Default `DATABRICKS_HOST`. */
  hostEnv?: string;
  /** Databricks CLI profile variable name. Default `DATABRICKS_CONFIG_PROFILE`. */
  profileEnv?: string;
}

/** Native Databricks SDK credentials for a governed Fabric principal. */
export function databricksCredentials(principal: DatabricksPrincipal): Credentials {
  if (principal.kind === "pat") return newPatCredentials(principal.token);
  if (principal.kind === "on-behalf-of") {
    return newTokenCredentials(
      "fabric-obo",
      tokenProviderFn(async () => ({ value: await principal.userToken(), type: "Bearer" })),
    );
  }
  if (principal.kind === "cli-profile") return cliProfileCredentials(principal);
  const source = newM2mCredentials({
    host: databricksWorkspaceOrigin(principal.host),
    clientId: principal.clientId,
    clientSecret: principal.clientSecret,
    ...(principal.scope ? { scopes: [principal.scope] } : {}),
  });
  return cachedTokenCredentials("oauth-m2m", source, (principal.refreshSkewSeconds ?? 60) * 1000);
}

export function databricksCredentialsFromTokenProvider(
  provider: DatabricksTokenProvider,
  name = "fabric-request",
): Credentials {
  return newTokenCredentials(
    credentialSourceName(name),
    tokenProviderFn(async () => ({ value: await provider(), type: "Bearer" })),
  );
}

function credentialSourceName(value: string): string {
  const normalized = value
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/gu, "-")
    .replace(/^-+|-+$/gu, "")
    .slice(0, 64);
  return /^[a-z]/u.test(normalized) ? normalized : `fabric-${normalized || "request"}`;
}

/** Builds a rotating bearer-token provider from the same native credential used by SDK clients. */
export function databricksIdentity(principal: DatabricksPrincipal): DatabricksTokenProvider {
  if (principal.kind === "pat") return async () => principal.token;
  if (principal.kind === "on-behalf-of") return async () => principal.userToken();
  return tokenProviderFromCredentials(databricksCredentials(principal));
}

/** Resolve a PAT or OAuth M2M principal consistently across CLIs, recipes, Apps, and tests. */
export function databricksPrincipalFromEnv(
  env: Record<string, string | undefined> = typeof process !== "undefined" ? process.env : {},
  options: DatabricksPrincipalEnvOptions = {},
): Extract<DatabricksPrincipal, { kind: "pat" | "service-principal" | "cli-profile" }> {
  const tokenName = options.tokenEnv ?? "DATABRICKS_TOKEN";
  const clientIdName = options.clientIdEnv ?? "DATABRICKS_CLIENT_ID";
  const clientSecretName = options.clientSecretEnv ?? "DATABRICKS_CLIENT_SECRET";
  const hostName = options.hostEnv ?? "DATABRICKS_HOST";
  const profileName = options.profileEnv ?? "DATABRICKS_CONFIG_PROFILE";
  const token = env.DATABRICKS_BEARER?.trim() || env[tokenName]?.trim();
  if (token) return { kind: "pat", token };
  const clientId = env[clientIdName]?.trim();
  const clientSecret = env[clientSecretName]?.trim();
  const rawHost = env[hostName]?.trim();
  if (clientId && clientSecret) {
    if (!rawHost) throw new Error(`Missing ${hostName} for Databricks OAuth M2M.`);
    return {
      kind: "service-principal",
      host: databricksWorkspaceOrigin(rawHost),
      clientId,
      clientSecret,
    };
  }
  const profile = env[profileName]?.trim() || env.DATABRICKS_PROFILE?.trim();
  if (profile || env.DATABRICKS_AUTH_MODE === "cli") {
    return {
      kind: "cli-profile",
      ...(rawHost ? { host: databricksWorkspaceOrigin(rawHost) } : {}),
      ...(profile ? { profile } : {}),
    };
  }
  throw new Error(
    `Set DATABRICKS_BEARER/${tokenName}, ${clientIdName} + ${clientSecretName}, or ${profileName} (${hostName} optional — the CLI profile provides the host).`,
  );
}

/**
 * Workspace host resolved from a Databricks CLI profile (`~/.databrickscfg` or
 * `DATABRICKS_CONFIG_FILE`), normalized to the workspace origin. `DATABRICKS_*`
 * environment variables overlay the file per the official SDK credential chain.
 */
export async function databricksHostFromCliProfile(profile?: string): Promise<string> {
  const resolved = await resolveDatabricksProfile({ ...(profile ? { profile } : {}) });
  if (!resolved.host) {
    throw new Error(
      `Databricks CLI profile "${resolved.name ?? profile ?? "DEFAULT"}" does not define a host; add one to the profile or set DATABRICKS_HOST.`,
    );
  }
  return databricksWorkspaceOrigin(resolved.host);
}

function cliProfileCredentials(
  principal: Extract<DatabricksPrincipal, { kind: "cli-profile" }>,
): Credentials {
  let resolved: Promise<Credentials> | undefined;
  return {
    name: () => "databricks-cli",
    async authHeaders() {
      resolved ??= resolveDatabricksProfile({
        ...(principal.profile ? { profile: principal.profile } : {}),
      }).then((profile: Profile) => {
        const host = principal.host ?? profile.host;
        if (!host) {
          throw new Error(
            `Databricks CLI profile "${profile.name ?? principal.profile ?? "DEFAULT"}" does not define a host; set \`host\` on the principal or add one to the profile.`,
          );
        }
        return defaultCredentials({ profile: { ...profile, host: databricksWorkspaceOrigin(host) } });
      });
      return (await resolved).authHeaders();
    },
  };
}

function cachedTokenCredentials(
  name: string,
  source: TokenCredentials,
  skewMs: number,
): TokenCredentials {
  let cached: Token | undefined;
  let inflight: Promise<Token> | undefined;
  const provider = tokenProviderFn(async () => {
    if (cached && (!cached.expiry || cached.expiry.getTime() - skewMs > Date.now())) return cached;
    inflight ??= source.token().finally(() => {
      inflight = undefined;
    });
    cached = await inflight;
    return cached;
  });
  return newTokenCredentials(name, provider);
}

function tokenProviderFromCredentials(credentials: Credentials): DatabricksTokenProvider {
  return async () => {
    const headers = await credentials.authHeaders();
    const authorization = headers.find(
      (header: Header) => header.key.toLowerCase() === "authorization",
    )?.value;
    const token = authorization?.replace(/^Bearer\s+/i, "").trim();
    if (!token) throw new Error("databricks auth: native SDK credential returned no bearer token.");
    return token;
  };
}

// ─── Identity propagation (v2, workstream C3) ───────────────────────────────

/**
 * The service principal a Databricks App runs as, from the standard app
 * runtime environment (`DATABRICKS_HOST` / `DATABRICKS_CLIENT_ID` /
 * `DATABRICKS_CLIENT_SECRET`). Returns `undefined` when any variable is
 * missing, so callers can fall back to explicit configuration.
 */
export function appServicePrincipalFromEnv(
  env: Record<string, string | undefined> = typeof process !== "undefined" ? process.env : {},
): Extract<DatabricksPrincipal, { kind: "service-principal" }> | undefined {
  const host = env.DATABRICKS_HOST;
  const clientId = env.DATABRICKS_CLIENT_ID;
  const clientSecret = env.DATABRICKS_CLIENT_SECRET;
  if (!host || !clientId || !clientSecret) return undefined;
  return {
    kind: "service-principal",
    host: databricksWorkspaceOrigin(host),
    clientId,
    clientSecret,
  };
}

/**
 * On-behalf-of principal from Databricks Apps user-authorization headers:
 * the platform forwards the signed-in user's access token as
 * `x-forwarded-access-token` (plus `x-forwarded-email`/`x-forwarded-user`).
 * Returns `undefined` when the request carries no user token (e.g. app
 * service-to-service traffic) so callers fall back to the app principal.
 */
export function onBehalfOfFromHeaders(
  headers: Headers | Record<string, string | string[] | undefined>,
): Extract<DatabricksPrincipal, { kind: "on-behalf-of" }> | undefined {
  const read = (name: string): string | undefined => {
    if (typeof (headers as Headers).get === "function")
      return (headers as Headers).get(name) ?? undefined;
    const value = (headers as Record<string, string | string[] | undefined>)[name];
    return Array.isArray(value) ? value[0] : value;
  };
  const token = read("x-forwarded-access-token");
  if (!token) return undefined;
  const label = read("x-forwarded-email") ?? read("x-forwarded-user");
  return { kind: "on-behalf-of", userToken: () => token, ...(label ? { label } : {}) };
}

export interface FabricPrincipalOverrides {
  id?: string;
  /** Unity Catalog principal name, when it differs from the id. */
  ucPrincipal?: string;
  displayName?: string;
  /** Mark a service principal as the hosting app's own identity. */
  asAppPrincipal?: boolean;
}

/**
 * Map a Databricks principal onto the SDK's {@link FabricPrincipal} so the
 * governed identity rides submissions → tool calls → lineage/cost records.
 * Never carries a token — ids and labels only.
 */
export function fabricPrincipalFor(
  principal: DatabricksPrincipal,
  overrides: FabricPrincipalOverrides = {},
): FabricPrincipal {
  if (principal.kind === "service-principal") {
    const id = overrides.id ?? principal.clientId;
    return {
      kind: overrides.asAppPrincipal ? "app-principal" : "service-principal",
      id,
      ucPrincipal: overrides.ucPrincipal ?? id,
      ...(overrides.displayName ? { displayName: overrides.displayName } : {}),
    };
  }
  const id =
    overrides.id ??
    (principal.kind === "cli-profile" ? principal.profile : principal.label) ??
    (principal.kind === "pat"
      ? "pat-user"
      : principal.kind === "cli-profile"
        ? "databricks-cli-user"
        : "on-behalf-of-user");
  return {
    kind: "user",
    id,
    ...(overrides.ucPrincipal ? { ucPrincipal: overrides.ucPrincipal } : {}),
    ...(overrides.displayName ? { displayName: overrides.displayName } : {}),
  };
}
